/**
 * Русский каталог. Структура повторяет `uz.ts` (source of truth):
 * пропущенный ключ роняет компиляцию TypeScript.
 *
 * Язык интерфейса — ТЗ X4: uz / ru / en.
 */
import type { Catalog } from './uz'

export const ru: Catalog = {
  common: {
    appName: 'MyGram Admin',
    cancel: 'Отменить',
    confirm: 'Подтвердить',
    save: 'Сохранить',
    saving: 'Сохранение…',
    delete: 'Удалить',
    close: 'Закрыть',
    retry: 'Повторить',
    search: 'Поиск',
    loading: 'Загрузка…',
    all: 'Все',
    none: 'Нет',
    copy: 'Копировать',
    copied: 'Скопировано',
    back: 'Назад',
    actions: 'Действия',
    reason: 'Причина',
    optional: '(необязательно)',
    apply: 'Применить',
    clear: 'Очистить',
    signOut: 'Выйти',
    profile: 'Профиль',
    theme: 'Тема',
    themeDark: 'Тёмная',
    themeLight: 'Светлая',
    language: 'Язык',
    never: 'Никогда',
    unknown: 'Неизвестно',
    yes: 'Да',
    no: 'Нет',
    planned: 'запланировано',
    plannedHint: 'Функция пока не работает',
  },

  auth: {
    signInTitle: 'MyGram Admin',
    signInSubtitle: 'Вход в панель управления',
    username: 'Логин',
    password: 'Пароль',
    signIn: 'Войти',
    signingIn: 'Вход…',
    invalidCredentials: 'Неверный логин или пароль',
    networkError: 'Не удалось подключиться к серверу. Проверьте сеть.',
    twoFactorCode: 'Код 2FA',
    twoFactorInvalid: 'Неверный код 2FA',

    ssoTitle: 'Единый вход (SSO)',
    ssoDesc: 'Вход через my.mygram.my. Провайдер ещё не подключён.',
    webauthnTitle: 'Аппаратная 2FA (WebAuthn)',
    webauthnDesc:
      'Ключ безопасности станет обязательным. Провайдер не выбран — пока используются логин и пароль.',

    sessionExpiredTitle: 'Сессия истекла',
    sessionExpiredDesc: 'В целях безопасности войдите заново.',
    sessionExpiredAction: 'Войти заново',

    idleLockedTitle: 'Заблокировано',
    idleLockedDesc: '30 минут без активности. Введите пароль, чтобы продолжить.',
    unlock: 'Разблокировать',

    roleSelectTitle: 'Выберите роль',
    roleSelectDesc: 'У вас несколько ролей. Выберите одну для этой сессии.',

    deviceTitle: 'Регистрация устройства',
    deviceDesc: 'Новое устройство требует подтверждения руководителя.',
    deviceFingerprint: 'Отпечаток устройства',
    devicePending: 'Запрос на подтверждение отправлен',
    deviceWaiting: 'Ожидается подтверждение руководителя. Вход невозможен, пока устройство не одобрено.',

    breakGlassTitle: 'Аварийный доступ (break-glass)',
    breakGlassDesc:
      'Все права открываются на 4 часа. Вся команда получает уведомление немедленно, каждое действие попадает в отдельный аудит.',
    breakGlassReason: 'Причина (обязательно)',
    breakGlassReasonHint: 'Почему нужен аварийный доступ? Этот текст уйдёт всей команде.',
    breakGlassWarning: 'Это действие с необратимыми последствиями. Используйте только в настоящей аварии.',
    breakGlassActivate: 'Активировать аварийный доступ',

    ipDeniedTitle: 'IP-адрес не разрешён',
    ipDeniedDesc: 'Войдите из офисной сети или через корпоративный VPN.',
    ipCurrent: 'Ваш IP-адрес',

    demoTitle: 'A1 — Экраны аутентификации',
    demoDesc:
      'Все 8 экранов A1. Серверного потока пока нет — экраны без живого подключения смотрят и проверяют здесь.',
    demoBackToList: '← К списку экранов A1',
    demoLiveNote: 'живой (в потоке входа)',
    demoOrphanNote: 'написан, но серверного потока нет',

    welcomeTitle: 'Добро пожаловать',
    welcomeDesc: 'Перед началом работы осталось несколько шагов.',
  },

  modules: {
    overviewDesc: (done: number, total: number) => `28 модулей · ${done}/${total} экранов вживую`,
    moduleDesc: (done: number, total: number) =>
      `${done}/${total} экранов вживую · остальные запланированы (ждут endpoint)`,
    live: 'вживую',
    planned: 'запланирован',
    plannedScreenDesc: 'Этот экран ещё не построен. Структура готова; для запуска нужно:',
    plannedNeeds: 'Нужно:',
    plannedGeneric: 'серверный endpoint или инфраструктура',
  },

  broadcast: {
    title: 'Рассылки',
    note: 'Составление массового объявления и история (отправка — отдельный подтверждённый шаг).',
    compose: 'Новая рассылка',
    titleField: 'Заголовок',
    bodyField: 'Текст',
    audience: 'Аудитория',
    all: 'Всем',
    active: 'Активным',
    staff: 'Персоналу',
    draft: 'Черновик',
    queued: 'В очереди',
    sent: 'Отправлено',
    failed: 'Ошибка',
    recipients: 'Получатели',
    created: 'Черновик сохранён',
    createFailed: 'Не удалось сохранить',
    draftNote: 'Сохраняется как черновик — никому не отправляется.',
    saveDraft: 'Сохранить черновик',
    card: 'Карточка рассылки',
    cancelled: 'Отменено',
    queue: 'В очередь',
    cancel: 'Отменить',
    test: 'Тест',
    actionOk: 'Готово',
    actionFailed: 'Не удалось выполнить',
    schedule: 'Запланировать',
    scheduledTitle: 'Запланированные',
    pushHealth: 'Push-статус',
    pushHealthNote: 'Статус push-провайдера: FCM, зарегистрированные токены, распределение.',
    fcm: 'FCM',
    fcmYes: 'Настроен',
    fcmNo: 'Не настроен',
    pushTokens: 'Push-токены',
    byProvider: 'По провайдеру',
    statusBreakdown: 'По статусу',
    templates: 'Шаблоны',
    tplNew: 'Новый шаблон',
    tplName: 'Название шаблона',
    tplSaved: 'Шаблон сохранён',
    tplDeleted: 'Шаблон удалён',
    policy: 'Политика рассылок',
    policyNote: 'Ограничение скорости и анти-спам правила.',
    throttle: 'Лимит в минуту',
    throttleHint: '0 = без лимита',
    dailyCap: 'Дневной лимит',
    dailyCapHint: '0 = без лимита',
    cooldown: 'Пауза (часы)',
    cooldownHint: 'Мин. часов между рассылками одному пользователю',
    antispam: 'Анти-спам включён',
    settingsSaved: 'Настройка сохранена',
    banner: 'Сервисный баннер',
    bannerNote: 'Технический баннер/объявление в приложении (устанавливает админ).',
    bannerText: 'Текст баннера',
    severity: 'Важность',
    info: 'Инфо',
    warning: 'Предупреждение',
    critical: 'Критично',
    bannerEnabled: 'Баннер включён',
    preview: 'Предпросмотр',
  },

  nav: {
    legal: 'Юридич.',
    verification: 'Верификация',
    support: 'Поддержка',
    dashboard: 'Обзор',
    modules: 'Модули',
    groups: 'Группы',
    finance: 'Финансы',
    bots: 'Боты',
    moderation: 'Модерация',
    users: 'Пользователи',
    geo: 'География',
    staff: 'Сотрудники',
    security: 'Безопасность',
    broadcast: 'Рассылки',
    auditLog: 'Журнал аудита',
    settings: 'Настройки',
    collapse: 'Свернуть панель',
    expand: 'Развернуть панель',
  },

  table: {
    columns: 'Столбцы',
    density: 'Плотность',
    densityCompact: 'Плотно',
    densityNormal: 'Обычно',
    densityComfortable: 'Свободно',
    filters: 'Фильтры',
    addFilter: 'Добавить фильтр',
    clearFilters: 'Очистить все',
    savedViews: 'Сохранённые представления',
    saveCurrentView: 'Сохранить текущее представление',
    manageViews: 'Управление представлениями',
    defaultView: 'Представление по умолчанию',
    export: 'Экспорт',
    refresh: 'Обновить',
    selected: (n: number) => `Выбрано: ${n.toLocaleString()}`,
    selectAllMatching: (n: number) => `Все по фильтру: ${n.toLocaleString()}`,
    clearSelection: 'Снять выделение',
    rowsPerPage: 'На странице',
    newRecords: (n: number) => `${n.toLocaleString()} новых ↑`,
    showNew: 'Показать',
    rangeLabel: (from: number, to: number, total: number) =>
      `${from.toLocaleString()}–${to.toLocaleString()} / ${total.toLocaleString()}`,
    prevPage: 'Назад',
    nextPage: 'Вперёд',
    revealPii: 'Раскрыть',
    piiRevealed: 'Раскрыто — действие записано в аудит',
    shortcuts: 'Горячие клавиши',
  },

  filter: {
    contains: 'содержит',
    equals: 'равно',
    startsWith: 'начинается с',
    gt: 'больше',
    lt: 'меньше',
    between: 'в диапазоне',
    is: 'это',
    isAny: 'один из',
    today: 'Сегодня',
    last7: '7 дней',
    last30: '30 дней',
    thisMonth: 'Этот месяц',
    lastMonth: 'Прошлый месяц',
    custom: 'Произвольный',
    from: 'с',
    to: 'по',
  },

  states: {
    emptyTitle: 'Ничего не найдено',
    emptyDesc: 'Измените фильтры или очистите поиск.',
    errorTitle: 'Не удалось загрузить',
    errorDesc: 'Запрос не выполнен.',
    permissionDeniedTitle: 'Нет доступа',
    permissionDeniedDesc: (perm: string) => `Для этого раздела нужно право «${perm}».`,
    requestAccess: 'Запросить доступ',
    notFoundTitle: 'Страница не найдена',
  },

  danger: {
    impactTitle: 'Влияние',
    impactCalculating: 'Расчёт влияния…',
    reversible: (window: string) => `Можно откатить в течение ${window}`,
    irreversible: 'Необратимо',
    reasonLabel: 'Причина',
    reasonRequired: 'Причина (обязательно)',
    reasonPlaceholder: 'Почему выполняется это действие?',
    reasonTooShort: (min: number) => `Введите минимум ${min} символов`,
    reasonPresetLabel: 'Быстрые причины',
    typeToConfirmLabel: (name: string) => `Введите «${name}» для подтверждения`,
    typeToConfirmMismatch: 'Введённый текст не совпадает',
    fourEyesTitle: 'Требуется подтверждение второго сотрудника',
    fourEyesDesc:
      'Действие не выполняется сразу. Создаётся заявка, её должен подтвердить другой сотрудник.',
    fourEyesCreate: 'Создать заявку',
    fourEyesPending: 'Ожидает подтверждения',
    waitPeriodTitle: 'Период ожидания',
    waitPeriodDesc: (min: number) =>
      `Действие выполнится через ${min} мин. В это время любой сотрудник безопасности может его остановить.`,
    notifyTeam: 'Вся команда получит уведомление',
    undo: 'Отменить',
    undoneToast: 'Действие отменено',
    level: (n: number) => `Уровень ${n}`,
    levelName: {
      1: 'Лёгкий',
      2: 'Средний',
      3: 'Тяжёлый',
      4: 'Критический',
      5: 'Аварийный',
    } as Record<number, string>,
  },

  rt: {
    title: 'Real-time монитор',
    note: 'Живые счётчики — обновление каждые 5 секунд.',
    active5m: 'Активны (5 мин)',
    msgMin: 'Сообщений / мин',
    signupMin: 'Регистраций / мин',
    msgHour: 'Сообщений / час',
    signupHour: 'Регистраций / час',
    series: 'Поток сообщений (последние 15 минут)',
    updated: 'Обновлено',
    pause: 'Пауза',
    resume: 'Продолжить',
  },

  userExtra: {
    ticketsTitle: 'Тикеты поддержки',
    noTickets: 'Нет тикетов',
    priority: 'Приоритет',
    reportsTitle: 'Жалобы (от и против)',
    direction: 'Направление',
    by: 'От него',
    against: 'На него',
    target: 'Цель',
    resolved: 'Решено',
    dismissed: 'Отклонено',
    openReport: 'Открыть',
    botsTitle: 'Принадлежащие боты',
    botUsername: 'Бот @username',
    botName: 'Имя бота',
    revoked: 'Отозван',
    giftsTitle: 'Подарки / Stars',
    giftsSent: 'Отправлено',
    giftsReceived: 'Получено',
    giftsCount: 'Всего подарков',
    relatedTitle: 'Связанные аккаунты',
    platform: 'Платформа',
    device: 'Устройство',
    lastActive: 'Последняя активность',
  },

  infra: {
    title: 'Дашборд инфраструктуры',
    note: 'Runtime Go-процесса + здоровье PostgreSQL — вживую, read-only.',
    runtime: 'Runtime (Go)',
    database: 'База данных',
    uptime: 'Аптайм',
    goroutines: 'Горутины',
    heap: 'Heap',
    heapAlloc: 'Heap выделено',
    heapSys: 'Heap система',
    dbSize: 'Размер БД',
    goVersion: 'Версия Go',
    numCpu: 'Ядра CPU',
    gcCount: 'Число GC',
    pgVersion: 'PostgreSQL',
    dbName: 'Имя БД',
    migration: 'Миграция',
    activeConns: 'Активные соединения',
    pool: 'Пул соединений',
    idle: 'простой',
  },

  myTasks: {
    title: 'Мои задачи',
    note: 'Назначенная вам работа',
    myTickets: 'Мои тикеты',
    approvals: 'Ждут моего подтверждения',
    noTickets: 'Нет открытых тикетов на вас',
    noApprovals: 'Нет four-eyes на подтверждение',
  },

  secDash: {
    title: 'Дашборд безопасности',
    note: 'Аудит, four-eyes и статус сотрудников — вживую.',
    fourEyesOpen: 'Открытые four-eyes',
    staffActive: 'Активные / всего',
    auditWindow: 'События аудита (окно)',
    topActions: 'Частые действия',
    recentAudit: 'Последний аудит',
    fullLog: 'Полный журнал',
  },

  supDash: {
    title: 'Дашборд поддержки',
    note: 'Тикеты по статусу и приоритету — вживую.',
    total: 'Всего тикетов (окно)',
    openish: 'Открыто / в ожидании',
    categories: 'Категории',
    byStatus: 'По статусу',
    byPriority: 'По приоритету',
    recent: 'Последние тикеты',
  },

  modDash: {
    title: 'Дашборд модерации',
    note: 'Ожидающая нагрузка всех очередей trust&safety — вживую.',
    openWorkload: 'Открытая нагрузка (всего в ожидании)',
    reports: 'Жалобы',
    tickets: 'Открытые тикеты',
    appeals: 'Апелляции',
    verifications: 'Верификация',
    legal: 'Юридические запросы',
  },

  dash: {
    users: 'Пользователи',
    usersHint: 'Удалённые аккаунты не учитываются',
    dau: 'Активные (24 часа)',
    mau: 'Активные (30 дней)',
    messagesToday: 'Сообщений (24 часа)',
    messages7d: 'Сообщений (7 дней)',
    dialogs: 'Диалоги',
    mediaFiles: 'Медиафайлы',
    messagesPerDay: 'Сообщений по дням (7 дней)',
    activation: 'Активация',
    activationHint: (sent: number, total: number) =>
      `${sent.toLocaleString()} из ${total.toLocaleString()} пользователей отправили первое сообщение`,
    registrations: 'Регистрации',
    regToday: 'Сегодня',
    regD7: '7 дней',
    regD30: '30 дней',
    inconsistentTitle: 'Числа сервера противоречат друг другу',
    inconsistentDesc: (d7: number, total: number) =>
      `«7 дней» (${d7.toLocaleString()}) больше общего числа пользователей (${total.toLocaleString()}). ` +
      `Причина: счётчик регистраций учитывает удалённые аккаунты, а общее число — нет. ` +
      `Значение ненадёжно, пока сервер не исправлен.`,
    noData: 'Нет данных за этот период',
    valuesTable: 'Таблица значений',
    tvTitle: 'Режим экрана',
    tvLive: 'Онлайн',
    tvOffline: 'Нет связи',
    tvUpdated: (time: string) => `Обновлено ${time}`,
    tvExit: 'Выход',
    growthTitle: 'Дашборд роста',
    topCountries: 'Регистрации по странам',
  },

  groups: {
    group: 'Группа',
    channel: 'Канал',
    members: 'Участники',
    forum: 'Форум',
    cardTitle: 'Карточка чата',
    about: 'Описание',
    owner: 'Владелец',
    admin: 'Админ',
    member: 'Участник',
    customTitle: 'Особый титул',
    joined: 'Присоединился',
    restricted: 'Ограничен',
    membersTitle: 'Список участников',
    admins: 'Админы',
    adminsTitle: 'Админы и права',
    joinRequests: 'Заявки на вступление',
    requestedAt: 'Запрошено',
    bio: 'Био',
    topics: 'Темы форума',
    general: 'General',
    pinned: 'Закреплено',
    closed: 'Закрыто',
    messages: 'История сообщений',
    messageText: 'Текст',
    views: 'Просмотры',
    edited: 'Изменено',
    deleted: 'Удалено',
    adminLog: 'Журнал админов',
    actor: 'Исполнитель',
    media: 'Медиа-галерея',
    mediaNote: 'Метаданные + blurhash; превью байтов ждёт media-proxy (follow-up).',
    mime: 'MIME',
    size: 'Размер',
    dimensions: 'Размеры',
    uploader: 'Загрузил',
    stats: 'Статистика',
    new7d: 'Новые участники (7 дней)',
    messages7d: 'Сообщения (7 дней)',
    messagesTotal: 'Всего сообщений',
    topPosters: 'Топ авторов',
    boosts: 'Статус бустов',
    boostLevel: 'Уровень',
    boostCount: 'Бустеры',
    boostedAt: 'Время буста',
    boostFree: 'Бусты MyGram бесплатны (без денег).',
    growth: 'График роста',
    botsTitle: 'Подключённые боты',
    settingsTitle: 'Настройки',
    reportsTitle: 'Жалобы',
    signMessages: 'Подписанные посты',
    slowMode: 'Медленный режим',
    joinByRequest: 'Вступление по заявке',
    contentProtected: 'Защита контента',
    historyVisible: 'История видна',
    forumMode: 'Режим форума',
    topicLayout: 'Вид тем',
  },

  account: {
    secTitle: 'Безопасность и доступ',
    secIdentity: 'Личность',
    secAccess: 'Доступ (RBAC)',
    sec2fa: 'Двухфакторная аутентификация',
    sec2faNote: 'Настройка требует backend endpoint (запланировано).',
    sec2faOn: 'Включено',
    sec2faOff: 'Не включено',
    sec2faEnable: 'Включить 2FA',
    sec2faDisable: 'Отключить',
    sec2faScan: 'Добавьте этот секрет в приложение-аутентификатор (Google Authenticator и т.п.), затем введите 6-значный код для подтверждения.',
    sec2faVerify: 'Подтвердить и включить',
    sec2faCode: '6-значный код',
    sec2faSecret: 'Секретный ключ',
    secDevices: 'Устройства и сессии',
    secDevicesNote: 'Управление сессиями требует backend endpoint (запланировано).',
    backendPending: 'Ожидает backend',
    notifTitle: 'Уведомления',
    notifNote: 'Локальные настройки браузера для оповещений админ-панели.',
    notifDesktop: 'Уведомления на рабочем столе',
    notifSound: 'Звук',
    notifReports: 'Новые жалобы',
    notifTickets: 'Новые тикеты',
    notifFourEyes: 'Запросы двух пар глаз',
    profile: 'Мой профиль',
    interface: 'Настройки интерфейса',
    shortcuts: 'Горячие клавиши',
    username: 'Логин',
    role: 'Роль',
    roles: 'Роли',
    logout: 'Выйти',
    language: 'Язык',
    theme: 'Тема',
    dark: 'Тёмная',
    light: 'Светлая',
    scNext: 'Следующая строка',
    scPrev: 'Предыдущая строка',
    scSelect: 'Выбрать',
    scRange: 'Выбрать диапазон',
    scOpen: 'Открыть',
    scPanel: 'Боковая панель',
    scSearch: 'Глобальный поиск',
    scFilter: 'Фокус на фильтр',
    scClose: 'Закрыть / отмена',
    scHelp: 'Список горячих клавиш',
    help: 'Помощь и руководство',
    helpIntro:
      'Админ-панель MyGram — операционная консоль: пользователи, группы/каналы, финансы, география, аналитика и безопасность. ID экрана по ТЗ показан рядом с заголовком.',
    helpModules: 'Реестр 614 экранов и прогресс',
    helpShortcuts: 'Список горячих клавиш',
  },

  loc: {
    glossaryTitle: 'Глоссарий',
    glossaryNote: 'Предпочтительные переводы терминов — используйте единообразно.',
    editorTitle: 'Редактор переводов',
    editorNote: 'Правки локальны (в браузере). Скачайте патч и закоммитьте — сохранение на сервере требует endpoint записи (запланировано).',
    downloadPatch: 'Скачать патч',
    qcTitle: 'Контроль качества',
    issue: 'Проблема',
    issueEmpty: 'Пусто',
    issuePlaceholder: 'Несовпадение плейсхолдеров',
    issueUntranslated: 'Без перевода',
    noIssues: 'Проблем не найдено',
    exportTitle: 'Импорт / экспорт',
    exportNote: 'Экспорт клиентского i18n-каталога в загружаемый формат. Функциональные значения (с параметрами) исключаются.',
    exportCount: (n: number) => `${n.toLocaleString()} ключей`,
    importNote: 'Импорт (запись переводов) требует endpoint записи — запланировано (DEPS lane A).',
    packsTitle: 'Языковые пакеты',
    pluralsTitle: 'Правила плюрализации',
    pluralsNote: 'Категории множественного числа CLDR по языкам — справка для переводчиков.',
    title: 'Список языков',
    note: 'Клиентские i18n-каталоги (uz/ru/en). Ключи закреплены TS-типом.',
    language: 'Язык',
    keys: 'Ключи',
    untranslated: 'Без перевода (~)',
    coverage: 'Покрытие',
    heuristic:
      '«Без перевода» = значение совпадает с английским (эвристика) — имена собственные (MIME, General) могут быть ложным срабатыванием.',
    keysTitle: 'Список ключей',
    missingTitle: 'Отсутствующие переводы',
    statsTitle: 'Статистика переводов',
    searchPh: 'Поиск по ключу или значению…',
    value: 'значение',
    namespace: 'Пространство',
    allTranslated: 'Всё переведено',
    showingCap: (n: number, total: number) => `Показано ${n.toLocaleString()} / ${total.toLocaleString()}`,
  },

  legal: {
    queue: 'Юридические запросы',
    queueNote: 'Регистрируйте входящие юридические/GDPR запросы и отслеживайте исполнение.',
    log: 'Добавить запрос',
    created: 'Запрос зарегистрирован',
    createFailed: 'Не сохранено',
    kind: 'Тип',
    authority: 'Запрашивающий орган',
    reference: 'Reference / дело №',
    jurisdiction: 'Юрисдикция',
    affected: 'Затронутый пользователь',
    details: 'Детали',
    fulfilled: 'Исполнено',
    rejected: 'Отклонено',
    card: 'Карточка запроса',
    updateStatus: 'Изменить статус',
    resolutionPlaceholder: 'Комментарий исполнения (необязательно)',
    statusChanged: 'Статус изменён',
    data: 'Данные',
    preservation: 'Приказ о сохранении',
    removal: 'Удаление контента',
    blocking: 'Блокировка',
    emergency: 'Экстренный',
    rejectedTitle: 'Отклонённые запросы',
    transparencyTitle: 'Отчёт о прозрачности',
    total: 'Всего',
    byKind: 'По типу',
  },

  verif: {
    queue: 'Заявки на верификацию',
    queueNote: 'Пользователи запрашивают галочку. При одобрении выдаётся официальный знак.',
    kind: 'Тип',
    kindIdentity: 'Личность',
    kindOrg: 'Организация',
    kindNotable: 'Известность',
    evidence: 'Доказательства',
    approved: 'Одобрено',
    rejected: 'Отклонено',
    registry: 'Реестр верифицированных',
    card: 'Карточка заявки',
    approve: 'Одобрить',
    reject: 'Отклонить',
    approveHint: 'При одобрении: пользователь получает официальный знак (is_official).',
    approvedToast: 'Заявка одобрена, официальный знак выдан',
    rejectedToast: 'Заявка отклонена',
    decideFailed: 'Решение не сохранено',
    rejectedTitle: 'Отклонённые заявки',
    statsTitle: 'Статистика верификации',
    total: 'Всего',
    byKind: 'По типу',
  },

  appeals: {
    queue: 'Апелляции',
    queueNote: 'Пользователи оспаривают решение о бане/страйке.',
    kind: 'Тип',
    kindBan: 'Бан',
    kindStrike: 'Страйк',
    granted: 'Удовлетворена',
    rejected: 'Отклонена',
    card: 'Карточка апелляции',
    decisionNote: 'Комментарий решения',
    decision: 'Решение',
    notePlaceholder: 'Комментарий решения (необязательно)',
    reject: 'Отклонить',
    grant: 'Удовлетворить',
    grantHint: 'При удовлетворении: бан → снимается, страйк → истекает.',
    grantedToast: 'Апелляция удовлетворена, санкция снята',
    rejectedToast: 'Апелляция отклонена',
    decideFailed: 'Решение не сохранено',
  },

  strikes: {
    title: 'История страйков',
    queue: 'Последние страйки',
    queueNote: 'Прогрессивные санкции. Страйки выдаются из решения по жалобе или вручную.',
    severity: 'Уровень',
    issuedBy: 'Выдал',
    active: 'Активен',
    expired: 'Истёк',
    activePoints: 'Активные баллы',
    issue: 'Выдать страйк',
    issueBtn: 'Выдать',
    issued: 'Страйк выдан',
    issueFailed: 'Не удалось выдать страйк',
    none: 'Страйков нет',
    statsTitle: 'Статистика ограничений',
    total: 'Всего',
  },

  support: {
    queue: 'Тикеты поддержки',
    queueNote: 'Тикеты приходят от пользователей. Открытые — сверху.',
    subject: 'Тема',
    open: 'Открыт',
    pending: 'В ожидании',
    closed: 'Закрыт',
    updated: 'Обновлён',
    card: 'Карточка тикета',
    staff: 'Сотрудник',
    user: 'Пользователь',
    replySent: 'Ответ отправлен',
    statusChanged: 'Статус изменён',
    actionFailed: 'Действие не выполнено',
    replyPlaceholder: 'Напишите ответ…',
    send: 'Отправить',
    slaTitle: 'Мониторинг SLA',
    overdueTitle: 'Просроченные тикеты',
    age: 'Возраст',
    days: (n: number) => `${n} дн`,
    fresh: 'Новые (<24ч)',
    aging: 'Стареют',
    breached: 'Просрочены',
  },

  mod: {
    queue: 'Очередь модерации',
    queueNote: 'Жалобы приходят от пользователей. Фильтруйте по категории/статусу.',
    category: 'Категория',
    priorityTitle: 'Приоритетная очередь',
    target: 'Объект',
    reporter: 'Жалобщик',
    reason: 'Причина',
    pending: 'В ожидании',
    resolved: 'Решено',
    dismissed: 'Отклонено',
    reportCard: 'Карточка жалобы',
    resolution: 'Комментарий решения',
    decision: 'Решение',
    resolutionPlaceholder: 'Какая мера принята (необязательно)',
    dismiss: 'Отклонить (нарушения нет)',
    resolve: 'Решить (меры приняты)',
    resolvedToast: 'Жалоба решена',
    dismissedToast: 'Жалоба отклонена',
    decideFailed: 'Решение не сохранено',
    banUser: 'Заблокировать + решить',
    bannedToast: 'Пользователь заблокирован, жалоба закрыта',
    groups: 'Группировка (по объекту)',
    groupsNote: 'Сколько жалоб на один объект — самые обжалованные сверху.',
    reportCount: 'Жалобы',
    latest: 'Последняя',
    reporterReliability: 'Надёжность жалобщика',
    reporterReliabilityNote: 'Высокий dismiss-rate = много необоснованных жалоб (ненадёжный источник).',
    dismissRate: 'Доля отклонений',
    catCsam: 'CSAM',
    catViolence: 'Насилие',
    catFraud: 'Мошенничество',
    catSpam: 'Спам',
    catCopyright: 'Авторское право',
    catDoxxing: 'Доксинг',
    catOther: 'Другое',
  },

  flags: {
    title: 'Feature-флаги',
    card: 'Карточка флага',
    key: 'Ключ',
    value: 'Значение',
    on: 'Включён',
    off: 'Выключен',
    changelog: 'Журнал изменений флагов',
    type: 'Тип',
    remoteTitle: 'Значения remote config',
    toggleTitle: 'Включение / выключение флагов',
    toggleFailed: 'Не удалось изменить флаг',
    killTitle: 'Kill-switch (аварийное отключение)',
    killNote: 'Немедленное отключение активных флагов. Каждое отключение пишется в аудит.',
    killEmpty: 'Активных флагов нет — отключать нечего.',
    kill: 'Отключить',
    killed: 'Флаг отключён',
    killConfirmTitle: 'Аварийное отключение флага',
    killConfirmDesc: (key: string) => `«${key}» немедленно отключится для ВСЕХ пользователей. Введите ключ для подтверждения.`,
    rolloutTitle: 'Постепенное развёртывание (%)',
    rolloutNote: 'Флаг включается для N% пользователей (стабильное распределение — при росте процента набор только расширяется).',
    rolloutSaved: (pct: number) => `Развёртывание установлено на ${pct}%`,
    historyTitle: 'История изменений флага',
    historyEmpty: 'Для этого флага нет истории (ещё не менялся).',
    pickFlag: 'Выберите флаг',
    current: 'текущее',
    restore: 'Откатить',
    restored: 'Значение восстановлено',
    restoreTitle: 'Восстановить прежнее значение',
    restoreDesc: (key: string, value: string) => `Флаг «${key}» будет возвращён к значению «${value}».`,
    segTitle: 'Сегмент аудитории флага',
    segPlatforms: 'Платформы',
    segCountries: 'Страны',
    segCountrySearch: 'Поиск страны (код или название)…',
    segNoneNote: 'Без ограничений — флаг виден везде (для включённых).',
    segActiveNote: 'Флаг будет включён ТОЛЬКО в выбранных странах/платформах.',
    segSaved: 'Сегмент сохранён',
    segCleared: 'Сегмент очищен (без ограничений)',
    segClear: 'Снять ограничение',
    depsTitle: 'Зависимости флага',
    depsPrereqs: 'Флаги-предпосылки',
    depsNoneNote: 'Без зависимостей — флаг независим.',
    depsActiveNote: (key: string) => `«${key}» включится ТОЛЬКО когда включены следующие флаги.`,
    depsSaved: 'Зависимости сохранены',
    depsCleared: 'Зависимости очищены',
    depsClear: 'Очистить зависимости',
    schemaTitle: 'Схема значения флага',
    schemaType: 'Тип',
    schemaValues: 'Допустимые значения',
    schemaValuesHint: 'Через запятую (например: light, dark, auto).',
    schemaNone: 'Без схемы',
    schemaValid: 'Соответствует',
    schemaInvalid: 'Не соответствует',
    schemaSaved: 'Схема сохранена',
    schemaCleared: 'Схема удалена',
    schemaClear: 'Удалить схему',
  },

  exp: {
    title: 'A/B эксперименты',
    create: 'Новый эксперимент',
    empty: 'Экспериментов пока нет.',
    on: 'Активен',
    off: 'Выключен',
    name: 'Название',
    nameHint: 'Только [a-z0-9_], 1–64 символа. После создания не меняется.',
    nameInvalid: 'Неверное название ([a-z0-9_])',
    enabled: 'Активен (вариант раздаётся клиенту)',
    variants: 'Варианты (ключ + вес)',
    addVariant: 'Добавить вариант',
    needTwo: 'Нужно минимум 2 варианта',
    saved: 'Эксперимент сохранён',
    saveFailed: 'Не удалось сохранить',
    deleted: 'Эксперимент удалён',
    deleteTitle: 'Удалить эксперимент',
    deleteDesc: (name: string) => `Эксперимент «${name}» будет удалён. Пользователи перестанут получать вариант.`,
    distribution: (n: number) => `Распределение (${n.toLocaleString()} выборочных пользователей — реальное назначение)`,
  },

  auditSearch: {
    title: 'Поиск по аудиту',
    anyAction: 'Любое действие',
    since: 'С',
    until: 'По',
    search: 'Искать',
    hint: 'Заполните фильтры и нажмите «Искать».',
    results: (n: number) => `${n.toLocaleString()} записей`,
  },

  suspStaff: {
    tab: 'Подозрительные',
    note: 'Сотрудники по объёму деструктивных действий (ban/delete/block/…); статистический выброс — «подозрительно».',
    flaggedCount: (n: number) => (n > 0 ? `${n} подозрительных` : 'подозрительных нет'),
    days: (n: number) => `${n} дн.`,
    destructive: 'Деструктивные',
    total: 'Всего',
    targets: 'Объекты',
    reasonHigh: 'Высокий объём',
    reasonOutlier: 'Аномалия',
  },

  metrics: {
    activeUsers: 'DAU / WAU / MAU',
    wau: 'WAU (7 дней)',
    wauNote: 'WAU (недельная активность) пока не отдаётся эндпоинтом analytics — follow-up.',
    newUsers: 'Новые пользователи',
    botEcosystem: 'Статистика экосистемы ботов',
    botsTotal: 'Всего ботов',
    botsOfficial: 'Официальные боты',
    botsActive: 'Активные боты',
    botsRevoked: 'Отозванные',
    botsMiniApp: 'С Mini App',
    botsBusiness: 'С Business',
    botsInline: 'С inline',
    botsGateway: 'В gateway-реестре',
    botApps: 'Mini App',
    botGames: 'Игры',
    botActiveUsers: 'Активные пользователи ботов',
    messageVolume: 'Объём сообщений',
    funnel: 'Анализ воронки',
    totalUsers: 'Всего пользователей',
    sentFirst: 'Отправили первое сообщение',
  },

  analytics: {
    byPlatform: 'По платформам',
    byChatType: 'По типам чатов',
    byVersion: 'По версиям',
    byAppVersion: 'По версии приложения',
    byOsVersion: 'По версии ОС',
    byLanguage: 'По языку',
    pointsEconomy: 'Экономика Ball',
    pointsNote: 'Агрегат по внутренней системе Ball (без денег, на основе активности).',
    pointsCirculating: 'Ball в обороте',
    pointsEarned: 'Всего заработано',
    pointsSpent: 'Всего потрачено',
    pointsHolders: 'Держатели',
    pointsSuspended: 'Заморожено',
    pointsPurchases: 'Покупки',
    pointsEarnByReason: 'Источники Ball',
    pointsSpendByItem: 'На что тратят',
    moderationKpi: 'KPI модерации',
    modPending: 'Ожидают',
    modResolved: 'Решено',
    modDismissed: 'Отклонено',
    modTotal: 'Всего жалоб',
    modCreated7d: 'Новых за 7д',
    modResolved7d: 'Решено за 7д',
    modAvgResolution: 'Ср. время решения',
    modByCategory: 'По категориям',
    modTrend: 'Новые жалобы',
    daysLabel: (d: number) => `${d} дн`,
    channelBoosts: 'Бусты каналов',
    channelBoostsNote: 'Поддержка каналов (бусты) — на основе Ball, без денег.',
    cbTotal: 'Всего бустов',
    cbChannels: 'Каналов с бустами',
    cbBoosters: 'Бустеров',
    cb7d: 'Бустов за 7д',
    cbTop: 'Топ каналов',
    cbTrend: 'Динамика бустов',
    topBots: 'Топ ботов',
    activity: 'Активность (по часам)',
    activityNote: 'Последние 30 дней — активность сообщений по дням недели × часам.',
    weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    sessionDuration: 'Длительность сессий',
    sessionNote: 'Срок жизни сессии устройства: от создания до последней активности (или отзыва).',
    totalSessions: 'Всего сессий',
    activeSessions: 'Активные сессии',
    avgSession: 'Среднее',
    medianSession: 'Медиана',
    durationDist: 'Распределение длительности',
    durLt1h: '<1 ч',
    dur1h1d: '1 ч–1 д',
    dur1d7d: '1–7 дн',
    dur7d30d: '7–30 дн',
    durGt30d: '>30 дн',
    durUnits: { d: 'д', h: 'ч', m: 'м' },
    retention: 'Retention (возвраты)',
    retentionNote: 'Rolling D1/D7/D30: доля зарегистрированных N дней назад, активных и спустя N+ дней.',
    churn: 'Churn (отток)',
    churnNote: 'Rolling D1/D7/D30: доля когорты, не вернувшаяся (1 − retention).',
    byWindow: 'По окну',
    channelGrowth: 'Рост каналов',
    channelGrowthNote: 'Число каналов и присоединений участников (за 30 дней, дневные ряды).',
    totalChannels: 'Всего каналов',
    totalChannelMembers: 'Всего участников',
    newChannels7d: 'Новые каналы (7 дн)',
    newJoins7d: 'Новые участники (7 дн)',
    newChannelsSeries: 'Новые каналы (по дням)',
    memberJoinsSeries: 'Присоединения (по дням)',
    supportKpi: 'Support KPI',
    supportKpiNote: 'Статус тикетов, время решения и разрезы (support_tickets, чистый агрегат).',
    ticketsTotal: 'Всего тикетов',
    ticketsOpen: 'Открытые',
    ticketsPending: 'В ожидании',
    ticketsClosed: 'Закрытые',
    ticketsNew7d: 'Новые (7 дн)',
    ticketsClosed7d: 'Закрытые (7 дн)',
    ticketAvgResolution: 'Среднее время решения',
    ticketMedianResolution: 'Медианное время решения',
    ticketByCategory: 'По категориям',
    ticketByPriority: 'По приоритету',
    newTicketsSeries: 'Новые тикеты (по дням)',
    adRevenue: 'Доход от рекламы',
    adRevenueNote: 'Чистый агрегат по рекламным кампаниям: доход (потрачено), бюджет, показы/клики, разрез по статусу/размещению.',
    adCampaigns: 'Кампании',
    adActive: 'Активные',
    adImpressions: 'Показы',
    adClicks: 'Клики',
    adRevenueTotal: 'Доход (потрачено)',
    adBudget: 'Бюджет',
    adCtr: 'CTR',
    adByStatus: 'По статусу',
    adByPlacement: 'По размещению (доход)',
    inviteLinks: 'Пригласительные ссылки',
    inviteLinksNote: 'Чистый агрегат по пригласительным ссылкам: число ссылок/использований, активные/отозванные, разрез по типу чата, ряд новых ссылок за 30 дней.',
    linksTotal: 'Всего ссылок',
    linksActive: 'Активные',
    linksRevoked: 'Отозванные',
    linkUses: 'Всего использований',
    linksNew7d: 'Новые (7 дн)',
    linkAvgUses: 'Среднее использований',
    linkUsesByType: 'По типу чата (использования)',
    newLinksSeries: 'Новые ссылки (по дням)',
    revenueDash: 'Дашборд дохода',
    revenueDashNote: 'Сводный доход платформы: реклама (потрачено), комиссия маркетплейса (завершённые сделки) и бот Stars. Без PII.',
    revTotal: 'Общий доход',
    revSourceAd: 'Реклама',
    revSourceMarket: 'Комиссия маркетплейса',
    revSourceStar: 'Бот Stars',
    revMarketGmv: 'GMV маркетплейса',
    revMarketTrades: 'Сделки',
    revStarCharges: 'Платежи Stars',
    revBySource: 'По источнику',
    revPerDay: 'Доход (по дням: маркет+Stars)',
    metricsDict: 'Словарь метрик',
    metricsDictNote: 'Единый источник метрик аналитики: каждая метрика, её исходная таблица и формула расчёта (техническое определение).',
    mdMetric: 'Метрика',
    mdSource: 'Источник (таблица)',
    mdFormula: 'Формула',
    arpuTitle: 'ARPU / ARPPU',
    arpuNote: 'Средний доход на пользователя (ARPU) и на платящего (ARPPU). Доход = реклама+комиссия маркетплейса+Stars; платящий = плательщик Stars ∪ покупатель маркетплейса. Без PII.',
    arpuPayers: 'Платящие',
    arpuValue: 'ARPU',
    arppuValue: 'ARPPU',
    arpuPayingRatio: 'Доля платящих',
    marketTitle: 'Аналитика маркетплейса',
    marketNote: 'Чистый агрегат по объявлениям, сделкам, продавцам, ставкам и отзывам маркетплейса. GMV = сумма сделок. Без PII.',
    marketDetailLink: 'Аналитика маркетплейса (подробно) →',
    mktListings: 'Объявления',
    mktActive: 'Активные',
    mktSold: 'Проданные',
    mktAuctions: 'Аукционы',
    mktSellers: 'Продавцы',
    mktVerified: 'Проверенные',
    mktTrades: 'Сделки',
    mktGmv: 'GMV',
    mktBids: 'Ставки',
    mktReviews: 'Отзывы',
    mktAvgRating: 'Средний рейтинг',
    mktByStatus: 'По статусу',
    mktNewListings: 'Новые объявления (по дням)',
    liveTitle: 'Аналитика Live/эфиров',
    liveNote: 'Чистый агрегат по эфирам, подаркам, зрителям, комментариям и баттлам. Сумма подарков = сумма live_gifts. Без PII.',
    liveDetailLink: 'Аналитика Live/эфиров →',
    liveStreams: 'Эфиры',
    liveLive: 'В эфире',
    liveHosts: 'Ведущие',
    liveLikes: 'Лайки',
    liveGifts: 'Подарки',
    liveGiftAmount: 'Сумма подарков',
    liveViewers: 'Зрители',
    liveComments: 'Комментарии',
    liveBattles: 'Баттлы',
    liveAvgDuration: 'Средняя длительность',
    liveByStatus: 'По статусу',
    liveGiftsSeries: 'Подарки (сумма по дням)',
    storiesTitle: 'Аналитика Stories',
    storiesNote: 'Чистый агрегат по историям и просмотрам: число историй/активных, авторы, просмотры, зрители, ряды за 30 дней. Без PII.',
    storiesDetailLink: 'Аналитика Stories →',
    storiesTotal: 'Истории',
    storiesActive: 'Активные',
    storiesAuthors: 'Авторы',
    storyViews: 'Просмотры',
    storyViewers: 'Зрители',
    storyAvgViews: 'Ср. просмотров',
    storiesSeries: 'Новые истории (по дням)',
    storyViewsSeries: 'Просмотры (по дням)',
    walletTitle: 'Экономика Wallet/Token',
    walletNote: 'Чистый агрегат по Take-кошельку (wallets) и пользовательским валютам (currencies/token). В обороте = сумма балансов кошельков. Отдельно от Балла. Без PII.',
    walletDetailLink: 'Экономика Wallet/Token →',
    walBalance: 'В обороте (Take)',
    walWallets: 'Кошельки',
    walTxns: 'Транзакции',
    walTransferVol: 'Объём переводов',
    walCurrencies: 'Валюты',
    walSupply: 'Общая эмиссия',
    walHolders: 'Держатели токенов',
    walTokenTxns: 'Токен-транзакции',
    walByKind: 'По типу',
    walTxnsSeries: 'Транзакции (по дням)',
    pollsTitle: 'Аналитика опросов',
    pollsNote: 'Чистый агрегат по опросам, викторинам и голосам: число опросов/открытых, викторина/аноним/мультивыбор, голоса, участники, ряды за 30 дней. Без PII.',
    pollsDetailLink: 'Аналитика опросов →',
    pollsTotal: 'Опросы',
    pollsOpen: 'Открытые',
    pollVotes: 'Голоса',
    pollVoters: 'Проголосовавшие',
    pollQuizzes: 'Викторины',
    pollAnon: 'Анонимные',
    pollMulti: 'Мультивыбор',
    pollOptions: 'Варианты',
    pollAvgVotes: 'Ср. голосов',
    pollsSeries: 'Новые опросы (по дням)',
    pollVotesSeries: 'Голоса (по дням)',
    reactTitle: 'Аналитика реакций',
    reactNote: 'Чистый агрегат по реакциям на сообщения: число реакций, уникальные эмодзи, реагирующие, сообщения с реакциями, ТОП-эмодзи, ряд за 30 дней. Без PII.',
    reactDetailLink: 'Аналитика реакций →',
    reactTotal: 'Реакции',
    reactEmojis: 'Уник. эмодзи',
    reactReactors: 'Реагирующие',
    reactMessages: 'Сообщений с реакциями',
    reactTopEmojis: 'ТОП эмодзи',
    reactSeries: 'Реакции (по дням)',
    mediaTitle: 'Аналитика медиа/хранилища',
    mediaNote: 'Чистый агрегат по файлам и вариантам: число файлов/готовых, владельцы, хранилище (байты), варианты, разрез по типу, ряд за 30 дней. Без PII.',
    mediaDetailLink: 'Аналитика медиа/хранилища →',
    mediaFiles: 'Файлы',
    mediaReady: 'Готовы',
    mediaOwners: 'Владельцы',
    mediaVariants: 'Варианты',
    mediaStorage: 'Хранилище',
    mediaVariantStorage: 'Хранилище вариантов',
    mediaByKind: 'По типу',
    mediaSeries: 'Новые файлы (по дням)',
    contactsTitle: 'Рост контактов',
    contactsNote: 'Чистый агрегат по импортированным контактам: число контактов, владельцы, совпавшие в приложении, доля совпадений, ряд за 30 дней. Без PII.',
    contactsDetailLink: 'Рост контактов →',
    contactsTotal: 'Контакты',
    contactsOwners: 'Владельцы',
    contactsMatched: 'Есть в приложении',
    contactsMatchRate: 'Доля совпадений',
    contactsSeries: 'Новые контакты (по дням)',
  },

  skeleton: {
    bannerTitle: 'Запланированный экран (скелет)',
    needs: 'Требуется',
    adapterReady: 'Каркас интерфейса + адаптер данных готовы — оживёт при подключении пайплайна.',
    needsEvents: 'Событийная телеметрия + ClickHouse (event-ETL)',
    eventDiscovery: 'Обзор событий',
    eventTaxonomy: 'Словарь событий (таксономия)',
    userPath: 'Путь пользователя (path)',
    funnelCompare: 'Сравнение воронок',
    segmentCompare: 'Сравнение сегментов',
    cohortBuilder: 'Конструктор когорт',
    forecasting: 'Прогнозирование',
    serviceQuality: 'Качество сервиса (SLO/SLI)',
    reportBuilder: 'Конструктор отчётов',
    needsObservability: 'Observability-пайплайн (Prometheus/Grafana + SLO-цели)',
    needsReportEngine: 'Движок конструктора отчётов (query-builder + экспорт)',
    alertsCenter: 'Центр оповещений',
    dashboardBuilder: 'Конструктор дашбордов',
    needsAlerting: 'Инфраструктура алертинга (правила + каналы)',
    needsDashboardEngine: 'Движок конструктора виджетов (drag-and-drop + сохранение)',
    needsPayments: 'Платёжный провайдер + AML-инфра (MyGram без денег)',
    needsRevenue: 'ETL доходов + данные монетизации',
  },

  sms: {
    title: 'SMS-провайдеры',
    note: 'Статус провайдера Infobip, реальный баланс и статистика отправленных SMS (страна/статус/последние). OTP-коды замаскированы.',
    balance: 'Баланс',
    totalSent: 'Всего отправлено',
    mode: 'Режим',
    notConfigured: 'Не настроен',
    byCountry: 'По странам',
    byStatus: 'По статусу',
    recent: 'Последние сообщения',
    colNumber: 'Номер',
    colMessage: 'Сообщение',
    colStatus: 'Статус',
    colTime: 'Время',
  },

  music: {
    title: 'Музыкальная библиотека',
    note: 'Модерация треков (одобрить/отклонить), пометка premium, удаление.',
    stAll: 'Все',
    stPending: 'На модерации',
    stApproved: 'Одобрено',
    stRejected: 'Отклонено',
    colTitle: 'Название',
    colArtist: 'Исполнитель',
    colGenre: 'Жанр',
    colDuration: 'Длительность',
    colPlays: 'Прослушивания',
    colStatus: 'Статус',
    approve: 'Одобрить',
    reject: 'Отклонить',
    premium: 'Premium',
    makePremium: 'Сделать premium',
    unpremium: 'Убрать premium',
    confirmDelete: 'Подтвердить?',
  },

  community: {
    title: 'Сообщества',
    note: 'Пользовательские наборы каналов/групп (владелец + число групп).',
    colName: 'Название',
    colOwner: 'Владелец',
    colGroups: 'Группы',
    colCreated: 'Создано',
  },

  users: {
    ban: 'Заблокировать',
    unban: 'Разблокировать',
    banLog: 'История банов',
    fairnessTitle: 'Отчёт о справедливости',
    fairnessNote: 'Соотношение банов и разблокировок — показатель справедливости модерации (GET /admin/audit).',
    unbanRatio: 'Доля разблокировок',
    activeRestrictions: 'Активные ограничения',
    bannedAt: 'Заблокирован',
    viewCard: 'Карточка',
    cardTitle: 'Карточка пользователя',
    userNumber: 'ID номер',
    email: 'Email',
    birthday: 'Дата рождения',
    bio: 'Био',
    lastSeen: 'Последняя активность',
    bannedReason: 'Причина бана',
    sessions: 'Сессии',
    sessionsTitle: 'Сессии и устройства',
    platform: 'Платформа',
    device: 'Устройство',
    osVersion: 'ОС',
    appVersion: 'Прил.',
    revoked: 'Отозвана',
    banTitle: (name: string) => `${name} — блокировка`,
    banDesc: 'Пользователь будет заблокирован, все его сессии немедленно прерваны. Причина обязательна.',
    forceLogout: 'Принудительный выход',
    forceLogoutTitle: (name: string) => `${name} — выход из всех сессий`,
    forceLogoutDesc:
      'ВСЕ сессии пользователя будут прерваны (без блокировки — может войти снова). Для подозрительного устройства/токена. Причина пишется в аудит.',
    forceLogoutPending: 'Действие заработает после деплоя бэкенда (ожидается патч).',
    actionDone: 'Выполнено',
    actionFailed: 'Действие не выполнено',
    groups: 'Группы/каналы',
    wallet: 'Кошелёк',
    ownedGroups: 'Группы/каналы во владении',
    contact: 'Контактные данные',
  },

  system: {
    title: 'Системные настройки',
    desc: 'Конфигурация платформы, которую приложение реально читает (app_settings)',
    save: 'Сохранить',
    saved: 'Настройки сохранены',
    saveError: 'Не удалось сохранить',
    loadError: 'Не удалось загрузить настройки',
    maintenance: 'Режим обслуживания',
    maintenanceDesc: 'Возвращает 503 на пользовательские запросы; админ, вход и внутренние сервисы работают.',
    maintenanceWarn: 'Приложение сейчас НЕДОСТУПНО для пользователей.',
    liveSection: 'Прямой эфир',
    streamerPct: 'Доля подарков стримеру (%)',
    maxCohosts: 'Макс. со-хостов',
    confirmMaintTitle: 'Включить режим обслуживания?',
    confirmMaint: 'Все пользовательские запросы будут возвращать 503, пока вы не выключите. Админы сохраняют доступ.',
    confirmMaintOk: 'Включить',
  },
  market: {
    title: 'Базар',
    sellerApps: 'Заявки продавцов',
    categories: 'Категории',
    shop: 'Магазин',
    region: 'Регион',
    activity: 'Деятельность',
    status: 'Статус',
    pending: 'На рассмотрении',
    approved: 'Одобрен',
    rejected: 'Отклонён',
    suspended: 'Заблокирован',
    none: 'Нет',
    approve: 'Одобрить',
    reject: 'Отклонить',
    verify: 'Отметить доверенным',
    unverify: 'Снять отметку',
    rejectReason: 'Причина отказа',
    rejectPrompt: 'Укажите причину отказа',
    rating: 'Рейтинг',
    deals: 'Сделки',
    verified: 'Доверенный',
    created: 'Дата',
    done: 'Готово',
    failed: 'Не выполнено',
    newCategory: 'Новая категория',
    editCategory: 'Изменить',
    addCategory: 'Добавить категорию',
    catTitle: 'Название',
    catIcon: 'Иконка (имя)',
    catPosition: 'Порядок',
    catEnabled: 'Активна',
    catListings: 'Объявления',
    catDisable: 'Скрыть',
    save: 'Сохранить',
    cancel: 'Отмена',
  },

  points: {
    title: 'Баллы',
    earnRules: 'Правила начисления',
    catalog: 'Каталог',
    suspicions: 'Подозрительные',
    event: 'Событие',
    amount: 'Количество',
    dailyCap: 'Дневной лимит',
    enabled: 'Активно',
    unlimited: 'Без лимита',
    edit: 'Изменить',
    save: 'Сохранить',
    cancel: 'Отмена',
    done: 'Готово',
    failed: 'Не выполнено',
    code: 'Код',
    itemTitle: 'Название',
    price: 'Цена',
    category: 'Категория',
    catCosmetic: 'Косметика',
    catBoost: 'Буст',
    catGift: 'Подарки',
    purchases: 'Покупки',
    newItem: 'Новый элемент',
    addItem: 'Добавить элемент',
    editItem: 'Изменить',
    disable: 'Скрыть',
    reason: 'Причина',
    score: 'Вес',
    clear: 'Оправдать',
    sanction: 'Заморозить',
    statusOpen: 'Открыто',
    statusCleared: 'Оправдан',
    statusSanctioned: 'Заморожен',
    user: 'Пользователь',
  },

  stickers: {
    moderation: 'Наборы стикеров',
    empty: 'Нет наборов стикеров',
    title: 'Набор',
    status: 'Статус',
    count: 'Стикеров',
    installs: 'Установок',
    reason: 'Причина',
    approve: 'Одобрить',
    reject: 'Отклонить',
    rejectPrompt: 'Укажите причину отклонения. Набор будет скрыт из магазина.',
    active: 'Активен',
    rejected: 'Отклонён',
    pending: 'Ожидает',
    done: 'Готово',
    failed: 'Не удалось',
    economyHint: 'Экономика',
  },

  finance: {
    amount: 'Сумма',
    note: 'Заметка',
    peer: 'Контрагент',
    idempotency: 'Ключ идемпотентности',
    wallets: 'Кошельки',
    balance: 'Баланс',
    updated: 'Обновлён',
    dashboard: 'Финансовый дашборд',
    dashboardNote: 'По окну, которое вернул сервер (топ-N кошельков + последние транзакции).',
    topWalletsSum: 'Сумма топ-кошельков',
    walletsShown: 'Показано кошельков',
    recentTxns: 'Последние транзакции',
  },

  bots: {
    official: 'Официальный',
    emptyTitle: 'Ботов нет',
    emptyDesc: 'Боты ещё не созданы. Появятся, когда бот-платформа их добавит.',
    card: 'Карточка бота',
    gatewayNote:
      'Владелец, статистика, webhook и история токенов появятся после подключения gateway бот-платформы (A5.03–A5.09).',
    statusLabel: 'Статус',
    active: 'Активен',
    disabled: 'Отключён',
    descLabel: 'Описание',
    aboutLabel: 'О боте',
    commandsLabel: 'Команды',
    noCommands: 'Нет команд',
    superadminOnly: 'Редактирование бота доступно только супер-админу.',
    edit: 'Редактировать',
    addCommand: 'Добавить команду',
    cmdName: 'Команда',
    ownerTitle: 'Владелец',
    noOwner: 'Владелец не назначен',
    webhookTitle: 'Webhook',
    noWebhook: 'Webhook не настроен',
    whPending: 'В очереди',
    whMaxConn: 'Макс. соединений',
    whLastError: 'Последняя ошибка',
    appsTitle: 'Mini Apps',
    noApps: 'Нет Mini Apps',
    businessTitle: 'Business-подключения',
    noBusiness: 'Нет business-подключений',
    affiliateTitle: 'Партнёрская программа',
    noAffiliate: 'Нет партнёрской программы',
    commission: 'Комиссия',
    duration: 'Срок (мес.)',
    gamesTitle: 'Игры',
    noGames: 'Нет игр',
    players: 'Игроки',
    reportsTitle: 'Жалобы',
    noReports: 'Жалоб нет',
    block: 'Заблокировать',
    blockTitle: 'Заблокировать бота',
    blockDesc: 'Аккаунт бота будет заблокирован (бан). Это сильное действие.',
    statsTitle: 'Статистика',
    statMsgs: 'Сообщения',
    statUpdates: 'Обновления',
    statToday: 'сегодня',
    appSettingsTitle: 'Тема Mini App',
    noAppSettings: 'Тема не настроена',
    themeBg: 'Цвет фона',
    themeHeader: 'Цвет заголовка',
    previewMediaTitle: 'Скриншоты / превью',
    noPreviewMedia: 'Нет превью-медиа',
    tokenTitle: 'API-ключ',
    noToken: 'Нет данных о токене',
    tokenActive: 'Активен',
    tokenRevoked: 'Отозван',
    tokenIssued: 'Выдан',
    tokenRotated: 'Обновлён',
    tokenRevokedAt: 'Отозван',
    makeOfficial: 'Сделать официальным',
    unmakeOfficial: 'Снять официальность',
    enable: 'Включить',
    disable: 'Отключить',
    officialTitle: 'Сделать бота официальным',
    officialDesc: 'Бот получит официальный знак (✓) и будет отмечен как официальный.',
    unofficialTitle: 'Снять официальность',
    unofficialDesc: 'С бота будет снят официальный знак.',
    enableTitle: 'Включить бота',
    enableDesc: 'Бот снова активируется и будет принимать запросы.',
    disableTitle: 'Отключить бота',
    disableDesc: 'Бот будет отключён и перестанет принимать запросы (обратимо).',
    actionDone: 'Готово',
    actionFailed: 'Не удалось выполнить',
  },

  security: {
    staffActivity: 'Активность сотрудников',
    actionCount: 'Кол-во действий',
    auditWindowNote: 'Агрегат по последним 200 записям аудита (не вся история).',
  },

  invites: {
    title: 'Ссылки-приглашения',
    usage: 'Использование',
    creator: 'Создатель',
    primary: 'Основная',
    expires: 'Срок',
  },

  geo: {
    sanctionsTitle: 'Санкционные списки',
    sanctionsNote: 'Страны из распространённых санкционных списков OFAC/EU, сверенные с реестром. «Review» = под санкциями, но не заблокирована.',
    compliance: 'Соответствие',
    compliant: 'Соответствует',
    reviewNeeded: 'Проверить',
    block: 'Заблокировать',
    unblock: 'Разблокировать',
    blockTitle: (name: string) => `Блокировка страны «${name}»`,
    unblockTitle: (name: string) => `Разблокировка страны «${name}»`,
    blockDesc: 'Пользователи этой страны потеряют доступ. Уровень 4: нужно подтверждение второго админа.',
    unblockDesc: 'Страна будет снова открыта. Уровень 4: нужно подтверждение второго админа.',
    impactUsers: 'зарегистрированных пользователей',
    reverseWindow: 'в любое время',
    requestCreated: 'Запрос four-eyes создан — ожидает подтверждения в очереди A18.06',
    requestFailed: 'Не удалось создать запрос',
    changelog: 'Журнал изменений',
    countryCard: 'Карточка страны',
    growth: 'Рост по странам',
    dialPrefixes: 'Телефонные префиксы',
    dialPrefixesNote: 'Сгруппировано по коду набора; общий префикс (+7 = RU/KZ) отмечен.',
    shared: 'Общий',
  },

  fourEyes: {
    title: 'Запросы four-eyes',
    subtitle: 'Действия уровня 4–5, ожидающие подтверждения второго сотрудника',
    tab: 'Four-eyes',
    requester: 'Запросил',
    approver: 'Подтвердил',
    level: 'Уровень',
    expires: 'Истекает',
    executeAfter: 'Можно выполнить',
    requiredRole: 'Требуемая роль',
    impact: 'Влияние (зафиксировано при запросе)',
    approve: 'Подтвердить',
    reject: 'Отклонить',
    cancel: 'Остановить',
    execute: 'Выполнить',
    actionDone: 'Выполнено',
    actionFailed: 'Действие не выполнено',
    cancelPrompt: 'Причина остановки (минимум 10 символов):',
    reasonShort: 'Причина должна быть не короче 10 символов',
    myRequests: 'Мои запросы',
    openOnly: 'Только открытые',
    selfApprovalBlocked: 'Нельзя подтвердить собственный запрос',
    executorIsRequester: 'Выполнить может только автор запроса',
    waitUntil: (t: string) => `Период ожидания истекает ${t}`,
    noEndpointTitle: 'Очередь не читается с сервера',
    noEndpointDesc:
      'Схема готова и проверена в базе (миграция 000076), но HTTP-метод для её чтения ещё не написан. Очередь не заполняется фиктивными данными.',
  },

  staff: {
    title: 'Сотрудники',
    subtitle: 'Роли, права и команды',
    member: 'Сотрудник',
    role: 'Роль',
    roles: 'Роли',
    team: 'Команда',
    status: 'Статус',
    lastActive: 'Последняя активность',
    createdAt: 'Добавлен',
    addMember: 'Добавить сотрудника',
    permissionMatrix: 'Матрица прав',
    rolesNote: 'Роли из клиентского каталога (заменится, когда придёт серверный каталог ролей). Создание/редактирование ролей ждёт серверный endpoint.',
    allPerms: 'все (*)',
    jitAccess: 'Временные права (JIT)',
    jitRemaining: (t: string) => `Временное право — осталось ${t}`,
    offboard: 'Отозвать права',
    suspend: 'Приостановить',
    emptyTitle: 'Список сотрудников пуст',
    emptyDesc: 'Добавьте первого сотрудника или импортируйте существующих.',
    superAdminWarning: (n: number) =>
      `Супер-админов: ${n}. По ТЗ их должно быть не более 2.`,
    inactive: 'Неактивен',
    permCount: 'Права',
    lastLogin: 'Последний вход',
    roleSaved: 'Роль обновлена',
    roleFailed: 'Не удалось изменить роль',
  },

  /** Заголовки столбцов таблиц — не хардкодятся в экранах. */
  col: {
    id: 'ID',
    name: 'Имя',
    username: '@username',
    phone: 'Телефон',
    status: 'Статус',
    registered: 'Регистрация',
    country: 'Страна',
    code: 'Код',
    nationality: 'Национальность',
    dialCode: 'Код набора',
    registrations: 'Регистраций',
    time: 'Время',
    staff: 'Сотрудник',
    action: 'Действие',
    target: 'Объект',
    detail: 'Детали',
    type: 'Тип',
    title: 'Название',
    ip: 'IP-адрес',
  },

  status: {
    active: 'Активен',
    pending: 'Ожидает',
    banned: 'Заблокирован',
    blocked: 'Заблокирован',
    deleted: 'Удалён',
    review: 'На рассмотрении',
    suspended: 'Приостановлен',
    open: 'Открыт',
    approved: 'Подтверждён',
    rejected: 'Отклонён',
    expired: 'Истёк',
    executed: 'Выполнен',
    cancelled: 'Остановлен',
  },

  time: {
    justNow: 'только что',
    secondsAgo: (n: number) => `${n} с назад`,
    minutesAgo: (n: number) => `${n} мин назад`,
    hoursAgo: (n: number) => `${n} ч назад`,
    daysAgo: (n: number) => `${n} дн назад`,
    monthsAgo: (n: number) => `${n} мес назад`,
    yearsAgo: (n: number) => `${n} г назад`,
  },

  env: {
    staging: 'Среда STAGING',
    dev: 'Среда DEV',
  },

  shortcuts: {
    title: 'Горячие клавиши',
    navigation: 'Навигация',
    nextRow: 'Следующая строка',
    prevRow: 'Предыдущая строка',
    select: 'Выделить',
    selectRange: 'Выделить диапазон',
    open: 'Открыть',
    detailPanel: 'Боковая панель',
    globalSearch: 'Глобальный поиск',
    focusFilter: 'Фокус на фильтр',
    primaryAction: 'Выполнить основное действие',
    escape: 'Закрыть / отменить',
    toggleSidebar: 'Переключить боковую панель',
    goHome: 'На главную',
    showShortcuts: 'Этот список',
  },
}
